import React, { useState } from 'react';
import Modal from '../Modal';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import PropTypes from 'prop-types';
import Loading from '../Loading';

function CSVUploader({
  triggerGetData,
  buttonClassName,
  buttonLabel = 'Upload CSV'
}) {
  const [loading, setLoading] = useState(false);
  const [, setData] = useState([]);
  const [tempData, setTempData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [columns, setColumns] = useState([]); // Store column names
  const [selectedEmailColumn, setSelectedEmailColumn] = useState(''); // Track email column
  const [selectedTagColumn, setSelectedTagColumn] = useState(''); // Track tag column

  const handleFileUpload = event => {
    const file = event.target.files[0];

    // Check if the file is valid and is a CSV file
    if (!file || !file.name.endsWith('.csv')) {
      console.error('Please upload a valid CSV file.');
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const text = reader.result;

      const lines = text.split('\n').filter(line => line.trim() !== '');
      const headers = lines[0].split(',').map(header => header.trim());

      const csvData = lines.slice(1).map(line => {
        const values = line.split(',').map(value => value.trim()); // Trim whitespace
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || ''; // Handle missing values
        });
        return obj;
      });

      setTempData(csvData);
      setColumns(headers); // Set columns after trimming headers
    };

    reader.onerror = err => {
      console.error('Error reading file:', err);
    };

    reader.readAsText(file); // Read the file as text
  };

  const handleClear = () => {
    setSelectedEmailColumn('');
    setSelectedTagColumn('');
    setTempData([]);
    setColumns([]);
  };

  const handleUpdateClick = async formData => {
    try {
      setLoading(true);

      const response = await sendRequest('/developers/upload/add', 'POST', {
        emailTagMappings: formData
      });

      if (response.success) {
        toast.success('Successfully added!');
        triggerGetData();
        setLoading(false);

        handleClear();
      }
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  const handleUseTempData = () => {
    if (!selectedEmailColumn || !selectedTagColumn || columns.length === 0) {
      toast.error('Please select both email and tag columns!');
      return;
    }

    const filteredData = tempData.map(row => ({
      email: row[selectedEmailColumn],
      tag: row[selectedTagColumn]
    }));

    if (
      filteredData.some(
        item =>
          String(item.tag || '')
            .trim()
            .toLowerCase() === 'ai agent'
      )
    ) {
      toast.error(
        'AI Agent can only be assigned from the developer edit screen.'
      );
      return;
    }

    handleUpdateClick(filteredData);

    setData(filteredData); // Save filtered data

    //setModalOpen(false); // Close the modal
  };

  const handleEmailColumnSelect = event => {
    setSelectedEmailColumn(event.target.value); // Update selected email column
  };

  const handleTagColumnSelect = event => {
    setSelectedTagColumn(event.target.value); // Update selected tag column
  };

  return (
    <>
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Upload CSV"
        >
          <div className="space-y-6">
            <div className="flex gap-2">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
            {loading && <Loading type="csv" />}

            {!loading && columns.length > 0 && (
              <>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="email-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Select Email Column:
                      </label>
                      <select
                        id="email-select"
                        value={selectedEmailColumn}
                        onChange={handleEmailColumnSelect}
                        className="border rounded px-3 py-2 w-full"
                      >
                        <option value="">-- Select Email Column --</option>
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="tag-select"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Select Tag Column:
                      </label>
                      <select
                        id="tag-select"
                        value={selectedTagColumn}
                        onChange={handleTagColumnSelect}
                        className="border rounded px-3 py-2 w-full"
                      >
                        <option value="">-- Select Tag Column --</option>
                        {columns.map(column => (
                          <option key={column} value={column}>
                            {column}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between mt-4">
                    <div className="flex gap-4">
                      <button
                        onClick={handleUseTempData}
                        className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
                      >
                        Use Data
                      </button>
                      <button
                        type="button"
                        onClick={handleClear}
                        className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-lg font-normal text-gray-200">Preview</h3>
                  <pre className="whitespace-pre-wrap break-words text-sm overflow-auto max-h-60 mt-4 bg-black text-white p-4 rounded-md">
                    {JSON.stringify(tempData.slice(0, 10), null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
      <button
        onClick={() => setModalOpen(true)}
        className={
          buttonClassName ||
          'mb-2 rounded bg-blue-600 px-2 py-1 text-sm text-white transition hover:bg-blue-700'
        }
        type="button"
      >
        {buttonLabel}
      </button>
    </>
  );
}

CSVUploader.propTypes = {
  triggerGetData: PropTypes.func.isRequired,
  buttonClassName: PropTypes.string,
  buttonLabel: PropTypes.string
};

export default CSVUploader;
